import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Ana sayfa kontrolü
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Ana Sayfa' },
    { path: '/hizmetler', label: 'Hizmetler' },
    { path: '/hakkimizda', label: 'Hakkımızda' },
    { path: '/iletisim', label: 'İletişim' },
  ];

  // Yazı rengini belirle: Ana sayfada scroll edilmemişse beyaz, diğer durumlarda lacivert
  const getTextColor = (isActive: boolean) => {
    if (isHomePage && !isScrolled) {
      // Ana sayfada scroll edilmemişse beyaz
      return isActive ? 'text-white' : 'text-cream-100 hover:text-white';
    }
    // Diğer durumlarda lacivert
    return isActive ? 'text-navy-900' : 'text-navy-600 hover:text-navy-900';
  };

  const getLogoTextColor = () => {
    if (isHomePage && !isScrolled) {
      return 'text-white';
    }
    return 'text-navy-900';
  };

  const getLogoSubTextColor = () => {
    if (isHomePage && !isScrolled) {
      return 'text-cream-200';
    }
    return 'text-navy-600';
  };

  const getDividerColor = () => {
    if (isHomePage && !isScrolled) {
      return 'bg-cream-300/30';
    }
    return 'bg-navy-200';
  };

  const getMobileButtonColor = () => {
    if (isHomePage && !isScrolled) {
      return 'text-white';
    }
    return 'text-navy-900';
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : isHomePage ? 'bg-transparent' : 'bg-white shadow-md'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/f7a5d02425e0675ec2bf06c3a77d9570/435f6bd6c74db995a4c06e524059bc0b.png"
              alt="Karşıyaka Visa Consulting Logo"
              className={`h-10 md:h-14 w-auto object-contain transition-all duration-300 ${
                isHomePage && !isScrolled ? 'brightness-0 invert' : ''
              }`}
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <Link
              to="/"
              className={`font-medium transition-colors whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-[#1e3a8a] hover:text-[#2563eb]'
                  : 'text-white hover:text-white/80'
              }`}
            >
              Ana Sayfa
            </Link>
            <Link
              to="/hizmetler"
              className={`font-medium transition-colors whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-[#1e3a8a] hover:text-[#2563eb]'
                  : 'text-white hover:text-white/80'
              }`}
            >
              Hizmetler
            </Link>
            <Link
              to="/blog"
              className={`font-medium transition-colors whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-[#1e3a8a] hover:text-[#2563eb]'
                  : 'text-white hover:text-white/80'
              }`}
            >
              Blog
            </Link>
            <Link
              to="/hakkimizda"
              className={`font-medium transition-colors whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-[#1e3a8a] hover:text-[#2563eb]'
                  : 'text-white hover:text-white/80'
              }`}
            >
              Hakkımızda
            </Link>
            <Link
              to="/iletisim"
              className={`font-medium transition-colors whitespace-nowrap ${
                isScrolled || !isHomePage
                  ? 'text-[#1e3a8a] hover:text-[#2563eb]'
                  : 'text-white hover:text-white/80'
              }`}
            >
              İletişim
            </Link>
            <a
              href="https://wa.me/905551234567?text=Merhaba%2C%20karsiyakavisaconsulting.com.tr%20web%20siteniz%20%C3%BCzerinden%20yaz%C4%B1yorum."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-4 xl:px-6 py-2 xl:py-2.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm xl:text-base"
            >
              WhatsApp
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 ${
              isScrolled || !isHomePage ? 'text-[#1e3a8a]' : 'text-white'
            }`}
            aria-label="Menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 bg-white border-t">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-[#1e3a8a] hover:text-[#2563eb] font-medium transition-colors"
              >
                Ana Sayfa
              </Link>
              <Link
                to="/hizmetler"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-[#1e3a8a] hover:text-[#2563eb] font-medium transition-colors"
              >
                Hizmetler
              </Link>
              <Link
                to="/blog"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-[#1e3a8a] hover:text-[#2563eb] font-medium transition-colors"
              >
                Blog
              </Link>
              <Link
                to="/hakkimizda"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-[#1e3a8a] hover:text-[#2563eb] font-medium transition-colors"
              >
                Hakkımızda
              </Link>
              <Link
                to="/iletisim"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-[#1e3a8a] hover:text-[#2563eb] font-medium transition-colors"
              >
                İletişim
              </Link>
              <a
                href="https://wa.me/905551234567?text=Merhaba%2C%20karsiyakavisaconsulting.com.tr%20web%20siteniz%20%C3%BCzerinden%20yaz%C4%B1yorum."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-6 py-2.5 rounded-lg font-semibold transition-all text-center whitespace-nowrap cursor-pointer"
              >
                WhatsApp ile İletişim
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;